// angular
import {
  AfterViewInit,
  booleanAttribute,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';
// components
import { VideoPreviewDialogComponent } from '@core/dialogs/video-preview-dialog/video-preview-dialog.component';
import { ImagePreviewDialogComponent } from '@app/core/dialogs/image-preview-dialog/image-preview-dialog.component';
import {
  buttonColors,
  ButtonComponent,
  buttonVariants,
} from '@core/components/button/button.component';
// services
import { UploaderService } from '@app/core/shared/services/uploader.service';
import { LoadingService } from '@shared/services/loading.service';
import { DialogService } from '@core/shared/services/dialog.service';
// directives & interfaces & pipes
import { DragDropDirective } from '@shared/directives/drag-drop.directive';
import { IUploadFile } from '@core/shared/interfaces/upload-file.interface';
import { ImageSourcePipe } from '@shared/pipes/image-source.pipe';

@Component({
  selector: 'pwj-upload-files',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    DragDropDirective,
    TranslateModule,
    ImageSourcePipe,
    NgxSkeletonLoaderModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadFilesComponent),
      multi: true,
    },
    {
      provide: NgControl,
      useExisting: forwardRef(() => UploadFilesComponent),
    },
    ImageSourcePipe,
  ],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
})
export class UploadFilesComponent
  implements OnInit, ControlValueAccessor, AfterViewInit
{
  label = input<string>('');
  subLabel = input<string>('');
  accept = input<'image' | 'video' | 'imageAndVideo'>('image');
  messages = input<{ [errorCode: string]: string }>({});
  isDisabled = input<boolean>(false);
  multiple = input(false, { transform: booleanAttribute });
  wFull = input(false, { transform: booleanAttribute });
  buttonsVariant = input<buttonVariants>();
  buttonsColor = input<buttonColors>('default');

  inputFile = viewChild<ElementRef | undefined>('fileInput');

  public images: IUploadFile[];
  public myForm: FormGroup;
  public progress: number = 0;
  public disabled: boolean = false;
  public hideButtons: boolean = true;
  public uploading: boolean = false;

  private _control!: NgControl;
  private _uploadSubscription: Subscription | null = null;

  private _isBlobFromAi: boolean = false;
  private _blobFromAi!: Blob;

  // dependencies
  private readonly _uploaderService = inject(UploaderService);
  private readonly _dialogService = inject(DialogService);
  private readonly _imageSourcePipe = inject(ImageSourcePipe);
  private readonly _injector = inject(Injector);
  public readonly loadingService = inject(LoadingService);

  constructor() {
    this.images = [];
    this.myForm = new FormGroup({
      images: new FormControl([], [Validators.required]),
    });
  }

  propagateChange = (_: any) => {};

  ngOnInit() {
    this._control = this._injector.get(NgControl);
  }

  ngAfterViewInit() {
    this.isDisabled() && this.setDisabledState(this.isDisabled());
  }

  get hasErrors(): boolean {
    return !!(
      this._control.errors &&
      (this._control.touched || this._control.dirty) &&
      this._control.invalid &&
      !!this.messages()
    );
  }

  get getErrors(): ValidationErrors | null {
    return this._control.errors;
  }

  get f() {
    return this.myForm.controls;
  }

  checkAccept(): string {
    switch (this.accept()) {
      case 'image':
        return '.jpg,.jpeg,.png,image/jpeg,image/png';
      case 'video':
        return 'video/mp4,video/webm,video/ogg';
      case 'imageAndVideo':
        return '.jpg,.jpeg,.png,image/jpeg,image/png,video/mp4,video/webm,video/ogg';
      default:
        return '.jpg,.jpeg,.png,image/jpeg,image/png';
    }
  }

  onFileChange(event: any) {
    const element = event.currentTarget as HTMLInputElement;
    let files: FileList | null = element.files;
    if (files) {
      this.addToImagesFormControl(files);
      this.generateImagePreviews(files);
      element.value = '';
    }
  }

  onFilesDropped(files: FileList) {
    if (!this.disabled) {
      this.addToImagesFormControl(files);
      this.generateImagePreviews(files);
    }
  }

  addToImagesFormControl(files: FileList) {
    if (this.multiple()) {
      const fileArray: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const fileItem = files.item(i);
        if (fileItem) {
          fileArray.push(fileItem);
        }
      }
      this.myForm.controls['images'].setValue([
        ...this.myForm.controls['images'].value,
        ...fileArray,
      ]);
    } else {
      this.myForm.controls['images'].setValue([files[0]]);
    }
  }

  generateImagePreviews(files: FileList) {
    this.hideButtons = false;
    this.uploading = true;
    if (this.multiple()) {
      Array.from(files).forEach(file => {
        this._fileReader(file);
      });
    } else {
      this.images = [];
      this._fileReader(files[0]);
    }
  }

  private _fileReader(file: File) {
    const reader = new FileReader();
    reader.onload = e => {
      if (this.accept() === 'image') {
        if (this.images && e.target) {
          this.images.push({ thumbnail: e.target.result as string });
        } else if (e.target) {
          this.images = [{ thumbnail: e.target.result as string }];
        }
      } else {
        if (this.images && e.target) {
          this.images.push({ url: e.target.result as string });
        } else if (e.target) {
          this.images = [{ url: e.target.result as string }];
        }
      }
    };
    reader.readAsDataURL(file);
  }

  private _blobToFile(theBlob: Blob, fileName: string) {
    return new File([theBlob], fileName, { type: 'image/png' });
  }

  removeImage(index: number) {
    if (!this.disabled) {
      this.images.splice(index, 1);
      this.myForm.controls['images'].value.splice(index, 1);
      this.propagateChange(this.images);
      if (this.images.length > 0) {
        this.images.forEach(val => {
          if (
            !val.url?.startsWith('data') &&
            !val.thumbnail?.startsWith('data')
          ) {
            this.submit();
          } else {
          }
        });
      } else {
        this.uploading = false;
        this.hideButtons = true;
      }
    }
  }

  public openImagePreviewDialog(src: string | undefined) {
    const processedUrl = this._imageSourcePipe.transform(src);
    const dialogRef = this._dialogService.open(ImagePreviewDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '70em',
      data: { thumbnail: processedUrl },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  public openVideoPreviewDialog(url: string | undefined) {
    const processedUrl = this._imageSourcePipe.transform(url);
    const dialogRef = this._dialogService.open(VideoPreviewDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '70em',
      data: { url: processedUrl },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  submit() {
    if (this.myForm.value.images.length <= 0) return;
    const formData = new FormData();
    if (this.myForm.value.images && !this._isBlobFromAi) {
      this.hideButtons = true;
      this.uploading = true;
      this.myForm.value.images.forEach((file: File, index: number) => {
        formData.append(`files`, file, file.name);
      });
    } else if (this._isBlobFromAi) {
      formData.append(`files`, this._blobFromAi);
    }
    this._uploadSubscription = this._uploaderService
      .uploadFile(formData)
      .subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.progress = Math.round((100 * event.loaded) / event.total!);
          } else if (event instanceof HttpResponse) {
            this.progress = 0;

            // old approach merged everything and could re-push duplicates
            const images: IUploadFile[] = [];
            this.images.forEach(img => {
              if (
                !img.thumbnail?.startsWith('data') &&
                !img.url?.startsWith('data')
              ) {
                images.push(img);
              }
            });
            event.body?.map((image: IUploadFile) => {
              images.push(image);
            });
            this.images = images;
            this.propagateChange(images);
            const returned: IUploadFile[] = event.body ?? [];
            this.images = returned;
            this.propagateChange(returned);
            this.uploading = false;
            this.hideButtons = true;
          }
        },
        error: err => {
          this.hideButtons = false;
          this.uploading = false;
        },
      });
  }

  cancel() {
    if (this._uploadSubscription) {
      this._uploadSubscription.unsubscribe();
      this._uploadSubscription = null;
      this.hideButtons = false;
      this.progress = 0;
    }
  }

  writeValue(files: IUploadFile[]): void {
    if (files?.length > 0) {
      files.forEach(f => {
        if (f?.thumbnail?.startsWith('data')) {
          this.addToImagesFormControl([
            this._blobToFile(f.thumbnail as unknown as Blob, 'wejoiner-ai.png'),
          ] as unknown as FileList);
          this.hideButtons = false;
        }
      });
      this.images = files;
    } else {
      this.images = [];
      this.resetUploader();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private resetUploader(): void {
    this.images = [];

    const control = this.myForm.controls['images'];
    control.setValue([]);

    control.markAsPristine();
    control.markAsUntouched();

    this.hideButtons = true;
    this.uploading = false;

    this.propagateChange([]);
  }
}
